import React from 'react';
import {
  QboModal,
  ITBox,
  QboFormGroup,
  QboSimpleDataTable,
  QboTextField,
  QboTypography,
} from '@ui/Components';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';
import useRunLicenseChecker from '@pages/TenantPage/M365Tenant/pagehooks/useRunLicenseChecker';
import useInvalidateServiceToken from '@pages/TenantPage/M365Tenant/pagehooks/useInvalidateServiceToken';
import useFeatureFlag from '@hooks/useFeatureFlag';
import useRunAutodiscover from '@pages/TenantPage/M365Tenant/pagehooks/useRunAutodiscover';
import useTenantSizeReport from '@pages/TenantPage/M365Tenant/pagehooks/useTenantSizeReport';
import useUserDiscrepancyReport from '@pages/TenantPage/M365Tenant/pagehooks/useUserDiscrepancyReport';
import QboSortByButton from '@ui/Components/QboSortByButton';
import { EMAIL } from '@pages/UserBackupPage/pageHooks/constants/emailConstants';
import useUserBackupReport from '@pages/UserBackupPage/pageHooks/useUserBackupReport';
import useRecoverPurgeMailbox from '@pages/UserBackupPage/pageHooks/useRecoverPurgeMailbox';
import useSyncM365Id from '@pages/UserBackupPage/pageHooks/useSyncM365Id';

export default function RequestModalTool({
  children,
  closeSetupModal,
  showRequestModal,
  topBarElement,
  title,
  selectedCredential,
  buttonDisabled,
  serviceType,
  selectedTenant,
  selectedAccount,
}) {
  const { featureFlags } = useFeatureFlag();
  const { t } = useTranslation();
  const isAstericRequired = true;
  let objectState = {};
  let m365IdList = [];
  let hookCommand = () => {};

  if (featureFlags.RunLicenseChecker === serviceType) {
    hookCommand = useRunLicenseChecker({
      selectedCredential,
      onSuccessRequest: closeSetupModal,
    });

    objectState = hookCommand.runLicenseCheckerState;
  }

  if (featureFlags.InvalidateServiceToken === serviceType) {
    hookCommand = useInvalidateServiceToken({
      selectedCredential,
      onSuccessRequest: closeSetupModal,
    });

    objectState = hookCommand.invalidateServiceTokenState;
  }

  if (featureFlags.RunAutodiscover === serviceType) {
    hookCommand = useRunAutodiscover({
      selectedCredential,
      onSuccessRequest: closeSetupModal,
    });

    objectState = hookCommand.runAutodiscoverState;
  }

  if (featureFlags.TenantSizeReport === serviceType) {
    hookCommand = useTenantSizeReport({
      selectedCredential,
      selectedTenant,
      onSuccessRequest: closeSetupModal,
    });

    objectState = hookCommand.tenantSizeReportState;
  }

  if (featureFlags.UserDiscrepancyReport === serviceType) {
    hookCommand = useUserDiscrepancyReport({
      selectedAccount,
      onSuccessRequest: closeSetupModal,
    });

    objectState = hookCommand.userDiscrepancyReportState;
  }

  if (featureFlags.UserBackupReport === serviceType) {
    hookCommand = useUserBackupReport({
      selectedAccount,
      onSuccessRequest: closeSetupModal,
    });

    objectState = hookCommand.userBackupReportState;
  }

  if (featureFlags.RecoverPurgeMailbox === serviceType) {
    hookCommand = useRecoverPurgeMailbox({
      selectedAccount,
      onSuccessRequest: closeSetupModal,
    });

    objectState = hookCommand.objectState;
  }

  if (featureFlags.SyncM365Id === serviceType) {
    hookCommand = useSyncM365Id({
      selectedAccount,
      onSuccessRequest: closeSetupModal,
    });

    objectState = hookCommand.objectState;
    m365IdList = hookCommand.m365IdList;
  }

  const { form, onValidateData } = hookCommand;

  const closeModal = () => {
    closeSetupModal();
    form.resetForm();
  };

  const reportTypeSelection = () => {
    if (featureFlags.TenantSizeReport !== serviceType) return null;

    return (
      <QboSortByButton
        marginRightValue={2}
        marginButtonValue={1}
        value={hookCommand.reportType}
        buttonWidth={220}
        startAdornmentTextWidth={200}
        startAdornmentText="Report Type"
        handleChange={(e) => {
          hookCommand.setReportType(e.target.value);
        }}
        menuItemList={[EMAIL]}
      />
    );
  };

  return (
    <QboModal
      onClose={closeModal}
      open={showRequestModal}
      title={title}
      showCloseIcon
      preventOutsideClick>
      <ITBox className="TransferSubscriptionFormPage__wrapper" direction="column">
        <QboFormGroup
          loadingButton={objectState.fetching}
          onSubmit={onValidateData}
          withGenerateAndResetButton
          handleOnResetButton={closeModal}
          secondButtonLabel="Cancel"
          firstButtonLabel="Submit"
          buttonDisabled={buttonDisabled}>
          {topBarElement}
          {children}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <QboTextField
                id="supportTicket"
                required={isAstericRequired}
                placeholder="Support Ticket"
                label="Support Ticket"
                fullWidth
                value={form.attributes.supportTicketAttributes.value}
                errorMessage={form.attributes.supportTicketAttributes.errorMessage}
                inputProps={{ maxLength: 225 }}
                onChange={(e) => {
                  form.attributes.supportTicketAttributes.handleChange(e.currentTarget.value);
                }}
              />
            </Grid>

            <Grid item xs={6}>
              <QboTextField
                id="escalationTicket"
                placeholder="Escalation Ticket"
                label="Escalation Ticket"
                fullWidth
                value={form.attributes.escalationTicketAttributes.value}
                onChange={(e) =>
                  form.attributes.escalationTicketAttributes.handleChange(e.currentTarget.value)
                }
                inputProps={{ maxLength: 225 }}
              />
            </Grid>
          </Grid>
          <QboTextField
            id="description"
            placeholder="Description"
            label="Description"
            fullWidth
            value={form.attributes.descriptionAttributes.value}
            onChange={(e) =>
              form.attributes.descriptionAttributes.handleChange(e.currentTarget.value)
            }
            inputProps={{ maxLength: 225 }}
          />
          {featureFlags.SyncM365Id === serviceType && m365IdList && (
            <FormControl className="SyncM365IdRadio__wrapper" variant="standard">
              <QboTypography sx={{ fontSize: 13, fontWeight: 700 }} variant="h6">
                {t('user_backup_page.sync_m365_id.found_list')}
              </QboTypography>
              <RadioGroup
                name="select-job-type"
                value={form.attributes.m365IdAttributes.value}
                onChange={(e) =>
                  form.attributes.m365IdAttributes.handleChange(e.currentTarget.value)
                }>
                {m365IdList &&
                  m365IdList?.map((btn) => (
                    <>
                      <FormControlLabel
                        key={btn?.id}
                        sx={{ fontSize: 15 }}
                        value={btn?.value}
                        control={<Radio />}
                        label={btn?.radioLabel}
                      />
                      <QboSimpleDataTable
                        header={[
                          { label: 'M356 Id', columnName: 'Id', size: 'sm' },
                          {
                            label: 'Display Name',
                            columnName: 'DisplayName',
                            size: 'sm',
                          },
                          {
                            label: 'User Principal Name',
                            columnName: 'UserPrincipleName',
                            size: 'sm',
                          },
                          { label: 'Email', columnName: 'Mail', size: 'sm' },
                        ]}
                        rows={[btn?.data]}
                      />
                    </>
                  ))}
              </RadioGroup>
            </FormControl>
          )}
          {!m365IdList && (
            <QboTypography color="text.primary" style={{ fontSize: '1.5rem' }}>
              {t('system_status_section.not_found_section.heading_text')}
            </QboTypography>
          )}
          {reportTypeSelection()}
        </QboFormGroup>
      </ITBox>
    </QboModal>
  );
}

RequestModalTool.propTypes = {
  closeSetupModal: PropTypes.func,
  showRequestModal: PropTypes.bool,
  title: PropTypes.string,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  topBarElement: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  selectedCredential: PropTypes.object,
  buttonDisabled: PropTypes.bool,
  serviceType: PropTypes.string,
  selectedTenant: PropTypes.object,
  selectedAccount: PropTypes.object,
};

RequestModalTool.defaultProps = {
  closeSetupModal: () => {},
  showRequestModal: false,
  title: '',
  children: '',
  topBarElement: '',
  selectedCredential: null,
  buttonDisabled: false,
  serviceType: '',
  selectedTenant: null,
  selectedAccount: null,
};
